// AbsLinearInequalityTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import TwoRangesOverlap from "../../../../components/visuals/TwoRangesOverlap";

/* ===== helpers ===== */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

function absLatex(A, B) {
  const ax = A === 1 ? "x" : A === -1 ? "-x" : A > 0 ? `${A}x` : `-${Math.abs(A)}x`;
  const bx = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;
  return `\\left|${ax} ${bx}\\right|`.replace(/\s+/g, " ").trim();
}
function latexIneq(A, B, kind, c) {
  return `${absLatex(A, B)}\\;${SIGN_LATEX[kind]}\\;${c}`;
}

// ułamki i liczby mieszane w LaTeX
function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; }
function toFrac(num, den) {
  if (den < 0) { num = -num; den = -den; }
  const g = gcd(num, den);
  return { n: num / g, d: den / g };
}
function fracToLatex({ n, d }) {
  const s = n < 0 ? "-" : "";
  const an = Math.abs(n);
  const int = Math.floor(an / d);
  const rem = an % d;
  if (rem === 0) return `${s}${int}`;
  return int === 0 ? `${s}\\dfrac{${rem}}{${d}}` : `${s}${int}\\dfrac{${rem}}{${d}}`;
}

// Zwraca posortowane granice: liczbowo (lowNum, highNum) i w LaTeX (lowTex, highTex)
function orderedBounds(A, B, c) {
  const vL = (-c - B) / A;          // -c → lewa „kandydat”
  const vR = ( c - B) / A;          //  c → prawa „kandydat”
  const texL = fracToLatex(toFrac(-c - B, A));
  const texR = fracToLatex(toFrac( c - B, A));
  if (vL <= vR) return { lowNum: vL, highNum: vR, lowTex: texL, highTex: texR };
  return { lowNum: vR, highNum: vL, lowTex: texR, highTex: texL }; // gdy A<0
}

/* ===== generator ===== */
export function generateTask(forceKind) {
  const randi = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  let A = 0;
  while (A === 0) A = randi(-8, 8);

  const B = randi(-12, 12);

  // nie generujemy już |.| < 0 (ani „< liczby ujemnej”)
  let kind = forceKind ?? SIGN_KINDS[randi(0, SIGN_KINDS.length - 1)];
  let c;
  if (kind === "lt") {
    c = randi(1, 10); // < 0 i =0 wykluczamy tutaj
  } else {
    c = randi(0, 10); // dla ≤, ≥, > może być 0
  }

  return {
    type: "AbsLinearInequalityTask",
    data: { A, B, c, kind, latex: latexIneq(A, B, kind, c) },
  };
}

/* ===== UI ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <InlineMath math={data.latex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={'x\\;\\in'} />
        </div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 2) ∪ (5, ∞) • (-3, 7) • [1, 4] • {3} • ℝ • ∅"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw = typeof userInput === "string" ? userInput : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== logika rozwiązania ===== */
function solutionFor({ A, B, c, kind }) {
  const sign = kind;
  const x0 = -B / A;

  if (c < 0) {
    if (sign === "lt" || sign === "le") return { kind: "empty" };
    return { kind: "all" };
  }
  if (c === 0) {
    if (sign === "lt")   return { kind: "empty" };
    if (sign === "le")   return { kind: "point", x0 };
    if (sign === "gt")   return { kind: "outsideHole", x0 }; // R \ {x0}
    return { kind: "all" }; // ≥
  }

  if (sign === "lt" || sign === "le") {
    const L = (-c - B) / A;
    const R = ( c - B) / A;
    const inclusive = sign === "le";
    return { kind: "between", seg: {
      L: Math.min(L, R), R: Math.max(L, R),
      Lclosed: inclusive, Rclosed: inclusive
    }};
  }

  const inc = sign === "ge";
  const bound1 = ( c - B) / A;
  const bound2 = (-c - B) / A;
  const r1 = Math.min(bound1, bound2);
  const r2 = Math.max(bound1, bound2);
  return {
    kind: "outside",
    inclusive: inc,
    left:  { L: -Infinity, R: r1, Rclosed: inc },
    right: { L: r2,        R: Infinity, Lclosed: inc },
  };
}

function intervalsEqualFlexible(expected, parsed) {
  if (!parsed) return false;

  if (expected.kind === "all") {
    return parsed.kind === "all" ||
      (parsed.kind === "intervals" && parsed.intervals.length === 1 &&
       parsed.intervals[0].left === -Infinity && parsed.intervals[0].right === Infinity);
  }
  if (expected.kind === "empty") {
    return parsed.kind === "empty" ||
      (parsed.kind === "intervals" && parsed.intervals.length === 0);
  }
  if (expected.kind === "point") {
    if (parsed.kind === "point") return Math.abs(parsed.value - expected.x0) <= 1e-9;
    if (parsed.kind === "intervals" && parsed.intervals.length === 1) {
      const I = parsed.intervals[0];
      return I.leftClosed && I.rightClosed &&
             Math.abs(I.left - expected.x0) <= 1e-9 &&
             Math.abs(I.right - expected.x0) <= 1e-9;
    }
    return false;
  }
  if (expected.kind === "outsideHole") {
    if (parsed.kind === "intervals" && parsed.intervals.length === 2) {
      const [A, B] = parsed.intervals;
      const aOK = A.left === -Infinity && Math.abs(A.right - expected.x0) <= 1e-9 && !A.rightClosed;
      const bOK = B.right === Infinity && Math.abs(B.left - expected.x0) <= 1e-9 && !B.leftClosed;
      return aOK && bOK;
    }
    return false;
  }

  if (parsed.kind !== "intervals") return false;
  const eq = (x, y, tol = 1e-9) => Math.abs(x - y) <= tol;
  const norm = (I) => ({ L: I.left, R: I.right, Lc: !!I.leftClosed, Rc: !!I.rightClosed });

  const expectList = [];
  if (expected.kind === "outside") {
    expectList.push({ L: -Infinity, R: expected.left.R,  Lc:false, Rc:!!expected.left.Rclosed });
    expectList.push({ L: expected.right.L, R: Infinity, Lc:!!expected.right.Lclosed, Rc:false });
  } else if (expected.kind === "between") {
    expectList.push({
      L: expected.seg.L, R: expected.seg.R,
      Lc: !!expected.seg.Lclosed, Rc: !!expected.seg.Rclosed
    });
  } else {
    return false;
  }

  const got = parsed.intervals.map(norm);
  if (expectList.length !== got.length) return false;

  const used = new Array(got.length).fill(false);
  for (const E of expectList) {
    let ok = false;
    for (let i = 0; i < got.length; i++) {
      if (used[i]) continue;
      const G = got[i];
      const Lok = (E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L));
      const Rok = (E.R ===  Infinity ? G.R ===  Infinity : eq(E.R, G.R));
      if (Lok && Rok && E.Lc === G.Lc && E.Rc === G.Rc) { used[i] = true; ok = true; break; }
    }
    if (!ok) return false;
  }
  return true;
}

export function checkAnswer({ data }, userInput = {}) {
  const raw = typeof userInput?.S === "string" ? userInput.S : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionFor(data);
  return { correct: intervalsEqualFlexible(ans, parsed) };
}

/* ===== poprawna odpowiedź (LaTeX w ułamkach/mieszanych) ===== */
export function renderAnswer({ data }) {
  const ans = solutionFor(data);

  const x0Frac = () => fracToLatex(toFrac(-data.B, data.A));
  const { lowTex, highTex } = orderedBounds(data.A, data.B, data.c);

  if (ans.kind === "all")   return <InlineMath math={'x\\in \\mathbb{R}'} />;
  if (ans.kind === "empty") return <InlineMath math={'x\\in \\varnothing'} />;
  if (ans.kind === "point") return <InlineMath math={`x\\in \\{${x0Frac()}\\}`} />;
  if (ans.kind === "outsideHole") {
    const X = x0Frac();
    return <InlineMath math={`x\\in (-\\infty,\\; ${X})\\;\\cup\\;(${X},\\; \\infty)`} />;
  }
  if (ans.kind === "between") {
    const lb = ans.seg.Lclosed ? "[" : "(";
    const rb = ans.seg.Rclosed ? "]" : ")";
    return <InlineMath math={`x\\in ${lb}${lowTex},\\; ${highTex}${rb}`} />;
  }
  // outside (unia dwóch półprostych, w poprawnej kolejności)
  const rb = ans.inclusive ? "]" : ")";
  const lb = ans.inclusive ? "[" : "(";
  return (
    <InlineMath math={`x\\in (-\\infty,\\; ${lowTex}${rb}\\;\\cup\\; ${lb}${highTex},\\; \\infty)`} />
  );
}

/* ===== pomoc do kroków — „dwie kolumny” rozwiązań liniowych ===== */
function solveLinearSide(A, B, rel, c) {
  // rozwiązuje Ax + B REL c  → kroki w LaTeX
  // rel ∈ { "<", "≤", ">", "≥" }
  const ax = A === 1 ? "x" : A === -1 ? "-x" : `${A}x`;
  const btxt = B === 0 ? "" : B > 0 ? `+ ${B}` : `- ${Math.abs(B)}`;

  const step1 = String.raw`${ax} ${btxt} \; ${rel} \; ${c}`;
  const step2 = String.raw`${ax} \; ${rel} \; ${c} ${B === 0 ? "" : (B > 0 ? `- ${B}` : `+ ${Math.abs(B)}`)}`;
  const rhs2 = B === 0 ? c : (B > 0 ? `${c - B}` : `${c + Math.abs(B)}`);
  // dzielenie przez A i ewentualne odwrócenie znaku
  const flip = A < 0;
  const rel3 =
    flip ? (rel === "<" ? ">" : rel === ">" ? "<" : rel === "≤" ? "≥" : "≤")
         : rel;
  const { n, d } = toFrac(Number(rhs2), A);
  const step3 = String.raw`x \; ${rel3} \; ${fracToLatex({ n, d })}`;

  return String.raw`\begin{aligned}
${step1}\\[2pt]
${step2}\\[2pt]
${step3}
\end{aligned}`;
}

/* ===== wyjaśnienie ===== */
export function renderExplanation({ data }) {
  const { A, B, c, kind, latex } = data;
  const ans = solutionFor(data);

  // relacje po rozbiciu |Ax+B|
  const rels = {
    lt: { left: ">", right: "<" },     // -c < Ax+B  i  Ax+B < c  (AND)
    le: { left: "≥", right: "≤" },     // -c ≤ Ax+B  i  Ax+B ≤ c  (AND)
    gt: { left: ">", right: "<" },     // Ax+B > c   lub Ax+B < -c (OR)
    ge: { left: "≥", right: "≤" },     // Ax+B ≥ c   lub Ax+B ≤ -c (OR)
  }[kind];

  // kroki dwóch stron
  const isAND = kind === "lt" || kind === "le";
  const leftBlock  = solveLinearSide(A, B, isAND ? rels.left  : rels.left,  isAND ? -c :  c);
  const rightBlock = solveLinearSide(A, B, isAND ? rels.right : rels.right, isAND ?  c : -c);

  // posortowane granice (numericzne i LaTeX) — odporne na A<0
  const { lowNum, highNum, lowTex, highTex } = orderedBounds(A, B, c);
  const X0tex = fracToLatex(toFrac(-B, A));

  // zapis odpowiedzi (jak w renderAnswer)
  let solLatex = "";
  if (ans.kind === "all") solLatex = String.raw`x\in \mathbb{R}`;
  else if (ans.kind === "empty") solLatex = String.raw`x\in \varnothing`;
  else if (ans.kind === "point") solLatex = String.raw`x\in \{${X0tex}\}`;
  else if (ans.kind === "outsideHole") solLatex = String.raw`x\in (-\infty,\; ${X0tex})\ \cup\ (${X0tex},\; \infty)`;
  else if (ans.kind === "between") {
    solLatex = String.raw`x\in ${ans.seg.Lclosed ? "[" : "("}${lowTex},\; ${highTex}${ans.seg.Rclosed ? "]" : ")"}`;
  } else {
    solLatex = String.raw`x\in (-\infty,\; ${lowTex}${ans.inclusive ? "]" : ")"}\ \cup\ ${ans.inclusive ? "[" : "("}${highTex},\; \infty)`;
  }

  // schemat rozbicia
  const scheme =
    isAND
      ? String.raw`-${c}\;${kind==="le"?"\\le":"<"}\; ${A===1?"x":A===-1?"-x":`${A}x`}${B===0?"":B>0?`+ ${B}`:`- ${Math.abs(B)}`} \;${kind==="le"?"\\le":"<"}\; ${c}`
      : String.raw`${A===1?"x":A===-1?"-x":`${A}x`}${B===0?"":B>0?`+ ${B}`:`- ${Math.abs(B)}`} \;${kind==="ge"?"\\ge":">"}\; ${c}\ \lor\ ${A===1?"x":A===-1?"-x":`${A}x`}${B===0?"":B>0?`+ ${B}`:`- ${Math.abs(B)}`} \;${kind==="ge"?"\\le":"<"}\; -${c}`;

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Rozbijamy nierówność z wartością bezwzględną zgodnie ze schematem.</p>
      <BlockMath math={String.raw`${latex}\quad \Longleftrightarrow \quad ${scheme}`} />

      {/* Dwie kolumny – dwa jednoczesne zadania liniowe */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {isAND ? "Lewa (koniunkcja)" : "Warunek 1"}
          </div>
          <BlockMath math={leftBlock} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {isAND ? "Prawa (koniunkcja)" : "Warunek 2"}
          </div>
          <BlockMath math={rightBlock} />
        </div>
      </div>

      <p>
        <strong>Krok 2:</strong> Na osi liczbowej pokazujemy{" "}
        {isAND ? "dwa zakresy i ich część wspólną (koniunkcja)." : "dwa zakresy i ich sumę (alternatywa)."}
      </p>

      <div style={{ maxWidth: 700, margin: "0.5rem auto" }}>
        {ans.kind === "point" ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { from: -B/A, to: -B/A, leftClosed: true, rightClosed: true,
                leftLabelLatex: X0tex, rightLabelLatex: X0tex }
            ]}
          />
        ) : ans.kind === "outsideHole" ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to: -B/A, rightClosed: false, rightLabelLatex: X0tex },
              { from: -B/A, leftClosed: false, leftLabelLatex: X0tex },
            ]}
          />
        ) : isAND ? (
          /* KONIUNKCJA: zakresy względem high/low + zacieniona część wspólna */
          <TwoRangesOverlap
            showZero
            lockPair
            pairFractions={[1/3, 2/3]}
            overlapColorPreset="pink"
            overlapOpacity={0.4}
            overlapBlur={2}
            ranges={[
              // (-∞, high]  oraz  [low, ∞) — domknięcie zależne od ≤ lub <
              { to:   highNum, rightClosed: kind === "le", rightLabelLatex: highTex },
              { from: lowNum,  leftClosed:  kind === "le", leftLabelLatex:  lowTex  },
            ]}
          />
        ) : (
          /* ALTERNATYWA: NumberLineRanges z uporządkowanymi granicami */
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to:   lowNum,  rightClosed: kind === "ge", rightLabelLatex: lowTex  },
              { from: highNum, leftClosed:  kind === "ge", leftLabelLatex:  highTex },
            ]}
          />
        )}
      </div>

      <p><strong>Krok 3:</strong> Zapisujemy rozwiązanie.</p>
      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <InlineMath math={solLatex} />
      </div>
    </div>
  );
}
